/**
 * Global Layer Manager
 * Manages z-index values for different UI layers to ensure proper stacking order
 */

// Base z-index values for different layer types
const LAYER_BASE_Z_INDEX = {
  popover: 1100, // Higher than navbar (1000)
  modal: 1200,
  drawer: 1250, // Slide-in panels: above modal, below tooltip + Dialog (1500)
  tooltip: 1300,
  dropdown: 1100,
  overlay: 1000,
};

/**
 * Get the z-index for a specific layer type
 * @param {string} layerType - The type of layer (e.g., 'popover', 'modal', 'tooltip')
 * @returns {number} The z-index value for the layer
 */
export function getLayerZIndex(layerType) {
  return LAYER_BASE_Z_INDEX[layerType] || 1000;
}

/**
 * Get the overlay root element for rendering portals
 * Creates the element if it doesn't exist
 * @returns {HTMLElement} The overlay root element
 */
export function getOverlayRoot() {
  let overlayRoot = document.getElementById("overlay-root");
  if (!overlayRoot) {
    overlayRoot = document.createElement("div");
    overlayRoot.id = "overlay-root";
    document.body.appendChild(overlayRoot);
  }
  return overlayRoot;
}

/**
 * Finds the nearest stacking context ancestor and returns its z-index
 * A stacking context is created by elements with:
 * - position !== "static"
 * - z-index !== "auto"
 * - transform !== "none"
 * - opacity !== "1"
 * - filter !== "none"
 * - perspective !== "none"
 * - mix-blend-mode !== "normal"
 * @param {HTMLElement} element - The element to start searching from
 * @returns {number} The z-index of the nearest stacking context, or 0 if none found
 */
export function getParentZIndex(element) {
  if (!element) return 0;

  let el = element;
  while (el && el !== document.body) {
    const style = window.getComputedStyle(el);
    const isStackingContext =
      style.position !== "static" ||
      style.zIndex !== "auto" ||
      style.transform !== "none" ||
      style.opacity !== "1" ||
      style.filter !== "none" ||
      style.perspective !== "none" ||
      style.mixBlendMode !== "normal";

    if (isStackingContext) {
      const z = parseInt(style.zIndex || "0", 10);
      return isNaN(z) ? 0 : z;
    }
    el = el.parentElement;
  }
  return 0; // fallback
}
